import { useCallback, useMemo, useRef } from 'react';
import { type TFunction, useTranslation } from 'react-i18next';
import { StackableCols } from '../../styles';
import type {
  AllowedCheckOption,
  LoggedActivityVariableType,
  SourceVariableType,
  VariableNameAndType,
  VariableValidation,
} from '../../types';
import {
  useVariableDescriptionContext,
  type VariableErrorType,
} from '../../VariableWizardContext';
import { AUTOMATION_VARIABLE_DATA_TYPES } from 'pages/AutomationEngine/constants';
import { SelectNoScrollCapture as Select } from '__components/Inputs/Select';
import type { LoggedActivityVariableSourceSubTypes } from 'pages/AutomationEngine/dialogs/VariableWizard/types';
import type {
  AutomationVariableDataType,
  Field,
  GenericOption,
} from 'pages/AutomationEngine/types';
import { fieldValidate, FieldValidation } from '../utilties';
import { useInputErrors } from '../useInputErrors';
import type { LoggedActivityValidationKeys } from '../ValidationKeys';
import { isEqual } from 'lodash';
import { useActivityFieldOptions } from '../hooks/useActivityFieldOptions';
import type {
  CheckFieldFromVariableCompatibilityFN,
  CheckVariableFromFieldCompatibilityFN,
} from '../../useVariableMetaData';
import { useActivityObjectOptions } from '../hooks/useActivityObjectOptions';
import { isUUIDcompatible } from '../../utilities';

type SourceType = GenericOption<LoggedActivityVariableSourceSubTypes>;

const getValueSourceOptions = (t: TFunction): SourceType[] => [
  {
    value: 'logged_by',
    label: t('Logged By'),
  },
  {
    value: 'logged_at',
    label: t('Logged At'),
  },
  {
    value: 'due_datetime',
    label: t('Due Date/Time'),
  },
  {
    value: 'record_association',
    label: t('Record Association'),
  },
  {
    value: 'activity_field_value',
    label: t('Activity Field Value'),
  },
  {
    value: 'logged_activity_id',
    label: t('Logged Activity ID'),
  },
  {
    value: 'scheduled_activity_id',
    label: t('Scheduled Activity ID'),
  },
  {
    value: 'activity_object_id',
    label: t('Activity Object ID'),
  },
];

const singleValueProperties: Field['properties'] = {
  allowAdd: false,
  allowMultiple: false,
  allowOptions: false,
  allowRelations: false,
  type: '',
  targetColumn: '',
};

const loggedActivityAllowedTypeMappings: Record<
  LoggedActivityVariableSourceSubTypes,
  AllowedCheckOption
> = {
  logged_at: {
    kizenField: { fieldType: 'datetime', properties: singleValueProperties },
    allowedCheckTypes: 'compatibility',
  },
  logged_by: {
    kizenField: {
      fieldType: 'team_selector',
      properties: singleValueProperties,
    },
    allowedCheckTypes: 'compatibility',
  },
  due_datetime: {
    kizenField: { fieldType: 'datetime', properties: singleValueProperties },
    allowedCheckTypes: 'compatibility',
  },
  record_association: {
    kizenField: {
      fieldType: 'relationship',
      properties: singleValueProperties,
    },
    allowedCheckTypes: 'compatibility',
  },
  activity_field_value: {
    kizenField: {
      fieldType: 'relationship',
      properties: singleValueProperties,
    },
    allowedCheckTypes: 'field',
  },
  logged_activity_id: {
    kizenField: {
      fieldType: 'relationship',
      properties: singleValueProperties,
    },
    allowedCheckTypes: 'compatibility',
  },
  scheduled_activity_id: {
    kizenField: {
      fieldType: 'relationship',
      properties: singleValueProperties,
    },
    allowedCheckTypes: 'uuid',
  },
  activity_object_id: {
    kizenField: {
      fieldType: 'relationship',
      properties: singleValueProperties,
    },
    allowedCheckTypes: 'uuid',
  },
};

const optionFilter = (
  value: LoggedActivityVariableSourceSubTypes,
  variable: VariableNameAndType,
  checkVariableFromFieldCompatibility: CheckFieldFromVariableCompatibilityFN
) => {
  if (!value || !variable) {
    return false;
  }

  const optionTypes = getSourceSubtypeAllowedTypes(value);

  if (optionTypes?.allowedCheckTypes === 'uuid') {
    return isUUIDcompatible(variable.dataType);
  }

  return optionTypes?.allowedCheckTypes === 'field'
    ? true
    : !!optionTypes?.kizenField &&
        checkVariableFromFieldCompatibility(
          {
            ...optionTypes.kizenField,
            relation:
              value === 'record_association' &&
              optionTypes.kizenField.fieldType === 'relationship'
                ? {
                    cardinality: 'many_to_one',
                    relatedObject: variable?.dataSubtype?.value!,
                  }
                : undefined,
          },
          variable
        );
};

// simple true false by type
export const getFilteredValueSourceOptions = (
  checkVariableFromFieldCompatibility: CheckVariableFromFieldCompatibilityFN,
  variable: VariableNameAndType,
  t: TFunction
) => {
  return getValueSourceOptions(t).filter(({ value }) =>
    optionFilter(value, variable, checkVariableFromFieldCompatibility)
  );
};

// return a list of acceptable source options
export const getFilteredValueSourceOptionDetails = (
  checkVariableFromFieldCompatibility: CheckVariableFromFieldCompatibilityFN,
  variable: VariableNameAndType,
  t: TFunction
) => {
  return getValueSourceOptions(t)
    .map(({ value }) => value)
    .filter((value) =>
      optionFilter(value, variable, checkVariableFromFieldCompatibility)
    );
};

export const getSourceSubtypeAllowedTypes = (
  sourceSubtype: LoggedActivityVariableSourceSubTypes | undefined
) =>
  sourceSubtype ? loggedActivityAllowedTypeMappings[sourceSubtype] : undefined;

const getDefaultState = (
  sourceSubtype: LoggedActivityVariableSourceSubTypes
): LoggedActivityVariableType => ({
  value: undefined,
  valueCategory: undefined,
  variableType: AUTOMATION_VARIABLE_DATA_TYPES.string,
  sourceSubtype,
  id: Date.now().toString(),
});

const showActivityFieldTypes: (
  | LoggedActivityVariableSourceSubTypes
  | undefined
)[] = ['activity_field_value'];

const showAssociatedObjectTypes: (
  | LoggedActivityVariableSourceSubTypes
  | undefined
)[] = ['record_association'];

const useComponentState = (
  values: SourceVariableType & {
    sourceErrors: VariableErrorType;
  },
  t: TFunction
) => {
  const {
    variable,
    updateVariableSource,
    activityLoggedContext,
    checkVariableFromFieldCompatibility,
    errors,
    setErrors,
  } = useVariableDescriptionContext();
  const { dataType, dataSubtype } = variable;

  const valueSourceOptions = useMemo(() => {
    return getFilteredValueSourceOptions(
      checkVariableFromFieldCompatibility,
      variable,
      t
    );
  }, [checkVariableFromFieldCompatibility, variable, t]);

  const valueSourceOptionsRef = useRef(valueSourceOptions);
  const dataSubtypeRef = useRef(dataSubtype);
  const dataTypeRef = useRef(dataType);
  const isArrayRef = useRef(variable.isArray);

  const activityLoggedOptions = activityLoggedContext?.activities;

  const {
    loggedActivityVariable = getDefaultState(valueSourceOptions[0]?.value),
    sourceErrors,
    id,
  } = values;

  const { sourceSubtype, valueCategory, value } = loggedActivityVariable!;

  const {
    validate: validateSourceSubtype,
    hasError: hasErrorSourceSubtype,
    clearError: clearSourceSubtypeError,
  } = useInputErrors<LoggedActivityValidationKeys>(
    id,
    'sourceSubtype',
    sourceErrors,
    errors,
    setErrors
  );

  const {
    validate: validateValueCategory,
    hasError: hasErrorValueCategory,
    clearError: clearValueCategoryError,
  } = useInputErrors<LoggedActivityValidationKeys>(
    id,
    'valueCategory',
    sourceErrors,
    errors,
    setErrors
  );

  const {
    validate: validateValueValue,
    hasError: hasErrorValueValue,
    clearError: clearValueError,
  } = useInputErrors<LoggedActivityValidationKeys>(
    id,
    'value',
    sourceErrors,
    errors,
    setErrors
  );
  const { showActivityField, showAssociatedObject } = useMemo(
    () => ({
      showActivityField:
        !!valueCategory && showActivityFieldTypes.includes(sourceSubtype),
      showAssociatedObject:
        !!valueCategory && showAssociatedObjectTypes.includes(sourceSubtype),
    }),
    [sourceSubtype, valueCategory]
  );

  const updateActivityLoggedByVariable = useCallback(
    (loggedActivityVariable: LoggedActivityVariableType) => {
      updateVariableSource({
        ...values,
        loggedActivityVariable,
      });
    },
    [updateVariableSource, values]
  );

  const setSourceSubtype = useCallback(
    ({ value }: SourceType | { value: undefined }) => {
      updateActivityLoggedByVariable({
        ...loggedActivityVariable,
        sourceSubtype: value,
        valueCategory: undefined,
        value: undefined,
      });
      clearSourceSubtypeError('sourceSubtype');
      clearValueCategoryError('valueCategory');
      clearValueError('value');
    },
    [
      clearSourceSubtypeError,
      clearValueCategoryError,
      clearValueError,
      loggedActivityVariable,
      updateActivityLoggedByVariable,
    ]
  );

  const setActivityLogged = useCallback(
    (valueCategory: GenericOption) => {
      updateActivityLoggedByVariable({
        ...loggedActivityVariable,
        valueCategory,
        value: undefined,
      });
      clearValueCategoryError('valueCategory');
      clearValueError('value');
    },
    [
      clearValueCategoryError,
      clearValueError,
      loggedActivityVariable,
      updateActivityLoggedByVariable,
    ]
  );

  // used by activity_field_value and record_association
  const setActivityValue = useCallback(
    (value: GenericOption | undefined) => {
      updateActivityLoggedByVariable({
        ...loggedActivityVariable,
        value,
      });
      clearValueError('value');
    },
    [clearValueError, loggedActivityVariable, updateActivityLoggedByVariable]
  );

  const getIntersection = (
    sourceSubtype: LoggedActivityVariableSourceSubTypes | undefined,
    variable: VariableNameAndType
  ) => {
    if (!sourceSubtype || !variable) {
      return false;
    }

    const optionTypes = getSourceSubtypeAllowedTypes(sourceSubtype);

    if (optionTypes?.allowedCheckTypes === 'uuid') {
      return isUUIDcompatible(variable.dataType);
    }

    if (optionTypes?.allowedCheckTypes === 'field') {
      return true;
    }
    return (
      !!optionTypes?.kizenField &&
      checkVariableFromFieldCompatibility(
        {
          ...optionTypes.kizenField,
          relation:
            sourceSubtype === 'record_association' &&
            optionTypes.kizenField.fieldType === 'relationship'
              ? {
                  cardinality: 'many_to_one',
                  relatedObject: variable?.dataSubtype?.value!,
                }
              : undefined,
        },
        variable
      )
    );
  };

  // we need reset the config if the sourceSubtype chnages and it's no longer supported
  if (!isEqual(valueSourceOptions, valueSourceOptionsRef.current)) {
    const intersection = getIntersection(sourceSubtype, variable);

    // if they don't intersect reset selection as it's in valid
    if (!intersection) {
      setSourceSubtype({ value: undefined });
    } else {
      if (dataType === 'entity') {
        // // ensure value is cleared if it doesn't match the choosen dataSubtype (Object)
        if (
          ['record_association', 'activity_field_value'].includes(
            sourceSubtype!
          ) &&
          typeof value === 'object' &&
          value?.entityId !== dataSubtype
        ) {
          setActivityValue(undefined);
        }
      } else {
        if (
          typeof value === 'object' &&
          !checkVariableFromFieldCompatibility(
            value.fieldType ? { fieldType: value.fieldType } : undefined,
            variable
          )
        ) {
          setActivityValue(undefined);
        }
      }
    }

    valueSourceOptionsRef.current = valueSourceOptions;
  }

  // whatch for changes in dataSubtype so we can clear value if it's not available
  if (dataSubtype !== dataSubtypeRef.current) {
    if (
      // ensure value is cleared if it doesn't match the chossen dataSubtype (Object)
      dataType === 'entity' &&
      ['record_association', 'activity_field_value'].includes(sourceSubtype!) &&
      typeof value === 'object' &&
      value?.entityId !== dataSubtype
    ) {
      setActivityValue(undefined);
    }
    dataSubtypeRef.current = dataSubtype;
  }

  // watch for changes in dataType so we can clear value if it's not available
  if (
    dataType !== dataTypeRef.current &&
    dataType !== 'entity' &&
    typeof value === 'object' &&
    !checkVariableFromFieldCompatibility(
      value.fieldType ? { fieldType: value.fieldType } : undefined,
      variable
    )
  ) {
    // ensure value is cleared if it doesn't match the chosen dataSubtype (Object)
    setActivityValue(undefined);
  }

  dataTypeRef.current = dataType;

  // watch for changes in isArray so we can clear value if it's no longer compatible
  if (
    variable.isArray !== isArrayRef.current &&
    typeof value === 'object' &&
    !checkVariableFromFieldCompatibility(
      value?.fieldType ? { fieldType: value.fieldType } : undefined,
      variable
    )
  ) {
    setActivityValue(undefined);
  }

  isArrayRef.current = variable.isArray;

  const { activityFieldOptions, loadingActivityFieldOptions } =
    useActivityFieldOptions(valueCategory?.value, variable);

  const { activityObjectOptions, loadingActivityObjectOptions } =
    useActivityObjectOptions(valueCategory, dataType, dataSubtype);

  return {
    valueSourceOptions,
    sourceSubtype,
    setSourceSubtype,
    validateSourceSubtype,
    hasErrorSourceSubtype,

    activityLoggedOptions,
    valueCategory,
    setActivityLogged,
    validateValueCategory,
    hasErrorValueCategory,

    showActivityField,
    loadingActivityFieldOptions,
    activityFieldOptions,
    value,
    setActivityValue,
    validateValueValue,
    hasErrorValueValue,

    showAssociatedObject,
    loadingActivityObjectOptions,
    activityObjectOptions,
  };
};

export const Component = (
  values: SourceVariableType & {
    sourceErrors: VariableErrorType;
    dataType: AutomationVariableDataType;
    dataSubtype: any;
  }
) => {
  const { t } = useTranslation();

  const {
    valueSourceOptions,
    sourceSubtype,
    setSourceSubtype,
    validateSourceSubtype,
    hasErrorSourceSubtype,

    activityLoggedOptions,
    valueCategory,
    setActivityLogged,
    validateValueCategory,
    hasErrorValueCategory,

    showActivityField,
    loadingActivityFieldOptions,
    activityFieldOptions,
    value,
    setActivityValue,

    showAssociatedObject,
    loadingActivityObjectOptions,
    activityObjectOptions,
    validateValueValue,
    hasErrorValueValue,
  } = useComponentState(values, t);

  return (
    <StackableCols columns={3} gutter="20px">
      <Select
        label={t('Value Source')}
        placeholder={t('Choose Value Source')}
        value={valueSourceOptions.find(({ value }) => value === sourceSubtype)}
        options={valueSourceOptions}
        onChange={setSourceSubtype}
        validate={validateSourceSubtype}
        error={hasErrorSourceSubtype}
        menuInline
        data-qa="logged-activity-value-source"
      />
      <Select
        label={t('Activity')}
        placeholder={t('Choose Activity')}
        value={valueCategory}
        options={activityLoggedOptions}
        onChange={setActivityLogged}
        validate={validateValueCategory}
        error={hasErrorValueCategory}
        menuInline
        data-qa="logged-activity-activity"
      />

      {showActivityField ? (
        <Select
          label={t('Activity Field')}
          placeholder={t('Choose Activity Field')}
          value={value}
          options={activityFieldOptions}
          onChange={setActivityValue}
          validate={validateValueValue}
          error={hasErrorValueValue}
          menuInline
          isLoading={loadingActivityFieldOptions}
          data-qa="logged-activity-activity-field"
        />
      ) : null}

      {showAssociatedObject ? (
        <Select
          label={t('Associated Object')}
          placeholder={t('Choose Associated Object')}
          value={value}
          options={activityObjectOptions}
          onChange={setActivityValue}
          validate={validateValueValue}
          error={hasErrorValueValue}
          menuInline
          isLoading={loadingActivityObjectOptions}
          data-qa="logged-activity-associated-object"
        />
      ) : null}
    </StackableCols>
  );
};

const getFieldsToValidate = (t: TFunction): FieldValidation => ({
  sourceSubtype: [{ type: 'required', fieldType: 'entity', isSelect: true }],
  valueCategory: [
    { type: 'required', fieldType: 'entity', isSelect: true },
    {
      type: 'deleted',
      fieldType: 'entity',
      deletedConditions: ['entity', 'employee'],
      deletedNameLookup: { entity: t('Activity'), fallback: t('Record') },
    },
  ],
  value: [
    {
      type: 'required',
      fieldType: 'entity', // static mainly
      condition: {
        sourceSubtype: ['record_association', 'activity_field_value'],
      },
      isSelect: true,
    },
    {
      type: 'deleted',
      fieldType: 'entity',
      deletedConditions: ['entity', 'employee'],
      deletedNameLookup: { entity: t('Activity Field'), fallback: t('Record') },
    },
  ],
});

export const validate = ({ values, checkRequired, t }: VariableValidation) => {
  const { loggedActivityVariable } = values;
  return fieldValidate(
    loggedActivityVariable,
    getFieldsToValidate(t),
    undefined,
    checkRequired,
    t
  );
};
